// 통합 로그인 페이지 - 학부모 + 학생

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

type Step = 'phone' | 'password' | 'confirm' | 'otp' | 'signup';
type Role = 'parent' | 'student' | null;

interface CheckPhoneResponse {
  exists: boolean;
  has_password: boolean;
  role: Role;
  display_name: string | null;
  message: string;
}

interface LoginResponse {
  success: boolean;
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    user_id?: string;
    name: string;
    phone: string;
    organization_id?: string;
  };
  role: 'parent' | 'student';
}

interface SignupResponse {
  success: boolean;
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    user_id?: string;
    name: string;
    phone: string;
  };
  role: 'parent' | 'student';
  message: string;
}

export default function UnifiedLoginPage() {
  const navigate = useNavigate();

  // 자동 로그인: 이미 토큰이 있으면 대시보드로 바로 이동
  React.useEffect(() => {
    const role = localStorage.getItem('unified_role');
    const token = localStorage.getItem(`${role || 'parent'}_access_token`);
    if (token && role) {
      const isInternalNetwork = window.location.pathname.startsWith('/studyroom');
      const prefix = isInternalNetwork ? '/studyroom' : '';
      if (role === 'student') {
        navigate(`${prefix}/student/dashboard`, { replace: true });
      } else {
        navigate(`${prefix}/parent/dashboard`, { replace: true });
      }
    }
  }, [navigate]);

  // Step 관리
  const [step, setStep] = useState<Step>('phone');

  // 입력 값
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [name, setName] = useState('');

  // 상태
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // 체크 결과
  const [checkResult, setCheckResult] = useState<CheckPhoneResponse | null>(null);

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return numbers.slice(0, 3) + '-' + numbers.slice(3);
    return numbers.slice(0, 3) + '-' + numbers.slice(3, 7) + '-' + numbers.slice(7, 11);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value));
  };

  // Step 1: 전화번호 확인
  const handleCheckPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const cleanPhone = phone.replace(/-/g, '');

      const response = await fetch(`${API_BASE_URL}/api/v1/unified-auth/check-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleanPhone }),
      });

      const data: CheckPhoneResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.message || '전화번호 확인에 실패했습니다.');
      }

      setCheckResult(data);

      // 디버그 로그
      console.log('[UnifiedLogin] check-phone 응답:', data);
      console.log('[UnifiedLogin] has_password:', data.has_password);
      console.log('[UnifiedLogin] role:', data.role);

      if (!data.exists) {
        setError(data.message);
        return;
      }

      if (data.has_password) {
        // 비밀번호가 설정된 사용자 -> 비밀번호 입력
        console.log('[UnifiedLogin] 비밀번호 화면으로 이동');
        setMessage(data.message);
        setStep('password');
      } else {
        // 비밀번호 미설정 -> OTP 인증 확인 화면으로 (자동 발송 안함)
        console.log('[UnifiedLogin] OTP 확인 화면으로 이동');
        setMessage(`${data.display_name}님, 최초 로그인입니다.\n인증번호를 발송하여 본인 확인을 진행합니다.`);
        setStep('confirm');
      }
    } catch (err: any) {
      console.error('Check phone error:', err);
      setError(err.message || '전화번호 확인에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // OTP 발송
  const sendOtp = async (cleanPhone: string, purpose: 'signup' | 'password_reset' = 'signup') => {
    const response = await fetch(`${API_BASE_URL}/api/v1/unified-auth/send-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: cleanPhone, purpose }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || '인증번호 발송에 실패했습니다.');
    }
  };

  // Step 2: 비밀번호 로그인
  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const cleanPhone = phone.replace(/-/g, '');

      // 1. 백엔드에서 비밀번호 검증
      const response = await fetch(`${API_BASE_URL}/api/v1/unified-auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleanPhone, password }),
      });

      const data: LoginResponse = await response.json();

      if (!response.ok) {
        throw new Error((data as any).detail || '로그인에 실패했습니다.');
      }

      // 2. Supabase 세션 생성 (Supabase 사용 시에만)
      if (supabase) {
        const emailDomain = data.role === 'student' ? 'student.studyroom.local' : 'parent.studyroom.local';
        const fakeEmail = `${cleanPhone}@${emailDomain}`;

        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: fakeEmail,
          password: password,
        });

        if (signInError) {
          const { error: signUpError } = await supabase.auth.signUp({
            email: fakeEmail,
            password: password,
          });

          if (!signUpError) {
            await supabase.auth.signInWithPassword({
              email: fakeEmail,
              password: password,
            });
          }
        }
      }

      // 3. 토큰 저장
      saveTokens(data);

      // 바로 대시보드로 이동 (환영 메시지 없이)
      goToDashboard(data);
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || '로그인에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 비밀번호 분실 -> OTP 발송
  const handleForgotPassword = async () => {
    setError('');
    setIsLoading(true);

    try {
      const cleanPhone = phone.replace(/-/g, '');
      await sendOtp(cleanPhone, 'password_reset');
      setMessage('비밀번호 재설정을 위한 인증번호가 발송되었습니다.');
      setStep('otp');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: OTP 인증 후 비밀번호 설정 화면으로
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const cleanPhone = phone.replace(/-/g, '');

      // OTP 검증
      const response = await fetch(`${API_BASE_URL}/api/v1/unified-auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: cleanPhone,
          code: otpCode,
          purpose: checkResult?.has_password ? 'password_reset' : 'signup',
        }),
      });

      const data = await response.json();

      if (!data.valid) {
        throw new Error(data.message || '인증에 실패했습니다.');
      }

      // 비밀번호 설정 화면으로
      setStep('signup');
    } catch (err: any) {
      console.error('Verify OTP error:', err);
      setError(err.message || '인증에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 4: 회원가입/비밀번호 설정 완료
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const cleanPhone = phone.replace(/-/g, '');
      const isPasswordReset = checkResult?.has_password;

      if (isPasswordReset) {
        // 비밀번호 재설정 (기존 계정)
        const response = await fetch(`${API_BASE_URL}/api/v1/unified-auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone: cleanPhone,
            new_password: password,
            otp_code: otpCode,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || '비밀번호 재설정에 실패했습니다.');
        }

        // 비밀번호 재설정 후 로그인 화면으로
        setStep('phone');
        setPassword('');
        setOtpCode('');
        setMessage('비밀번호가 재설정되었습니다. 새 비밀번호로 로그인해주세요.');
      } else {
        // 신규 회원가입
        const response = await fetch(`${API_BASE_URL}/api/v1/unified-auth/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone: cleanPhone,
            password,
            name: checkResult?.role === 'parent' ? name : undefined,
            otp_code: otpCode,
          }),
        });

        const data: SignupResponse = await response.json();

        if (!response.ok) {
          throw new Error((data as any).detail || '가입에 실패했습니다.');
        }

        // 토큰 저장
        saveTokens(data);

        // 바로 대시보드로 이동 (환영 메시지 없이)
        goToDashboard(data);
      }
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(err.message || '처리에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 토큰 저장
  const saveTokens = (data: LoginResponse | SignupResponse) => {
    const prefix = data.role === 'parent' ? 'parent' : 'student';
    localStorage.setItem(`${prefix}_access_token`, data.access_token);
    localStorage.setItem(`${prefix}_refresh_token`, data.refresh_token);
    localStorage.setItem(`${prefix}_info`, JSON.stringify(data.user));
    localStorage.setItem('unified_role', data.role);
  };

  // 인증번호 재발송
  const handleResendOtp = async () => {
    setError('');
    setIsLoading(true);

    try {
      const cleanPhone = phone.replace(/-/g, '');
      const purpose = checkResult?.has_password ? 'password_reset' : 'signup';
      await sendOtp(cleanPhone, purpose);
      setMessage('인증번호가 재발송되었습니다.');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 대시보드로 이동 (내부망 경로 처리)
  const goToDashboard = (data: LoginResponse | SignupResponse) => {
    const isInternalNetwork = window.location.pathname.startsWith('/studyroom');
    const prefix = isInternalNetwork ? '/studyroom' : '';

    if (data.role === 'student') {
      navigate(`${prefix}/student/dashboard`, { replace: true });
    } else {
      navigate(`${prefix}/parent/dashboard`, { replace: true });
    }
  };

  const roleLabel = checkResult?.role === 'student' ? '학생' : '학부모';
  const themeColor = checkResult?.role === 'student' ? 'blue' : 'purple';

  return (
    <div className={`min-h-screen bg-gradient-to-br from-${themeColor}-50 via-white to-${themeColor}-50`}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className={`text-2xl font-bold bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 bg-clip-text text-transparent`}>
                NewAttend
              </div>
              {checkResult?.role && (
                <span className={`text-sm text-${themeColor}-600 font-medium`}>
                  {roleLabel}
                </span>
              )}
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors"
            >
              홈으로
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">

            {/* Step 1: 전화번호 입력 */}
            {step === 'phone' && (
              <>
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-gray-900">로그인</h2>
                  <p className="mt-2 text-sm text-gray-600">
                    전화번호를 입력해주세요
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleCheckPhone} className="space-y-4">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      전화번호
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      placeholder="010-1234-5678"
                      value={phone}
                      onChange={handlePhoneChange}
                      disabled={isLoading}
                      maxLength={13}
                      autoFocus
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed text-lg"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      독서실에 등록된 전화번호를 입력하세요
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || phone.replace(/-/g, '').length < 10}
                    className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-md hover:shadow-lg"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        확인 중...
                      </span>
                    ) : (
                      '다음'
                    )}
                  </button>
                </form>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
                  <p className="font-semibold text-gray-900 mb-2">로그인 안내</p>
                  <div className="space-y-1 text-gray-700">
                    <p>- 학생: 본인 전화번호로 로그인</p>
                    <p>- 학부모: 등록된 부모님 전화번호로 로그인</p>
                  </div>
                </div>
              </>
            )}

            {/* Step 2: 비밀번호 입력 */}
            {step === 'password' && (
              <>
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-gray-900">{roleLabel} 로그인</h2>
                  <p className="mt-2 text-sm text-gray-600">
                    {checkResult?.display_name}님, 비밀번호를 입력해주세요
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      비밀번호
                    </label>
                    <input
                      id="password"
                      type="password"
                      placeholder="비밀번호 입력"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                      autoFocus
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all disabled:bg-gray-100"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || password.length < 6}
                    className={`w-full bg-${themeColor}-600 text-white py-3 rounded-lg font-semibold hover:bg-${themeColor}-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-md hover:shadow-lg`}
                  >
                    {isLoading ? '로그인 중...' : '로그인'}
                  </button>
                </form>

                <div className="flex justify-between items-center text-sm">
                  <button
                    onClick={() => { setStep('phone'); setPassword(''); }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ← 전화번호 변경
                  </button>
                  <button
                    onClick={handleForgotPassword}
                    disabled={isLoading}
                    className={`text-${themeColor}-600 hover:text-${themeColor}-700 font-medium`}
                  >
                    비밀번호 찾기
                  </button>
                </div>
              </>
            )}

            {/* Step 2.5: OTP 발송 확인 (최초 가입) */}
            {step === 'confirm' && (
              <>
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-gray-900">본인 인증</h2>
                  <p className="mt-2 text-sm text-gray-600 whitespace-pre-line">
                    {message}
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div className={`bg-${themeColor}-50 border border-${themeColor}-200 rounded-lg p-4`}>
                  <p className={`text-${themeColor}-800 text-sm`}>
                    <strong>{checkResult?.role === 'student' ? '학생' : '학부모'}</strong>으로 확인되었습니다.
                    <br />
                    카카오톡으로 인증번호를 발송합니다.
                  </p>
                </div>

                <button
                  onClick={async () => {
                    setError('');
                    setIsLoading(true);
                    try {
                      const cleanPhone = phone.replace(/-/g, '');
                      await sendOtp(cleanPhone, 'signup');
                      setMessage(`${checkResult?.display_name}님, 카카오톡으로 인증번호가 발송되었습니다.`);
                      setStep('otp');
                    } catch (err: any) {
                      setError(err.message || '인증번호 발송에 실패했습니다.');
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading}
                  className={`w-full bg-${themeColor}-600 text-white py-3 rounded-lg font-semibold hover:bg-${themeColor}-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-md hover:shadow-lg`}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      발송 중...
                    </span>
                  ) : (
                    '인증번호 발송'
                  )}
                </button>

                <button
                  onClick={() => { setStep('phone'); setError(''); }}
                  className="w-full text-gray-500 hover:text-gray-700 text-sm py-2"
                >
                  ← 전화번호 변경
                </button>
              </>
            )}

            {/* Step 3: OTP 입력 */}
            {step === 'otp' && (
              <>
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-gray-900">인증번호 입력</h2>
                  <p className="mt-2 text-sm text-gray-600">
                    {message}
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <div>
                    <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                      인증번호 6자리
                    </label>
                    <input
                      id="otp"
                      type="text"
                      placeholder="000000"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      disabled={isLoading}
                      maxLength={6}
                      autoFocus
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all disabled:bg-gray-100 text-center text-2xl tracking-widest"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || otpCode.length !== 6}
                    className={`w-full bg-${themeColor}-600 text-white py-3 rounded-lg font-semibold hover:bg-${themeColor}-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-md hover:shadow-lg`}
                  >
                    {isLoading ? '확인 중...' : '확인'}
                  </button>
                </form>

                <div className="flex justify-between items-center text-sm">
                  <button
                    onClick={() => setStep('phone')}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ← 전화번호 변경
                  </button>
                  <button
                    onClick={handleResendOtp}
                    disabled={isLoading}
                    className={`text-${themeColor}-600 hover:text-${themeColor}-700 font-medium`}
                  >
                    인증번호 재발송
                  </button>
                </div>
              </>
            )}

            {/* Step 4: 비밀번호 설정 */}
            {step === 'signup' && (
              <>
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-gray-900">비밀번호 설정</h2>
                  <p className="mt-2 text-sm text-gray-600">
                    {checkResult?.has_password ? '새 비밀번호를 입력해주세요' : '로그인에 사용할 비밀번호를 설정해주세요'}
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSignup} className="space-y-4">
                  {/* 학부모만 이름 입력 */}
                  {checkResult?.role === 'parent' && !checkResult?.has_password && (
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        이름
                      </label>
                      <input
                        id="name"
                        type="text"
                        placeholder="홍길동"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={isLoading}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all disabled:bg-gray-100"
                      />
                    </div>
                  )}

                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      비밀번호 (6자 이상)
                    </label>
                    <input
                      id="newPassword"
                      type="password"
                      placeholder="비밀번호 입력"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                      autoFocus
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all disabled:bg-gray-100"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      다음 로그인 시 사용할 비밀번호입니다
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || password.length < 6 || (checkResult?.role === 'parent' && !checkResult?.has_password && !name)}
                    className={`w-full bg-${themeColor}-600 text-white py-3 rounded-lg font-semibold hover:bg-${themeColor}-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-md hover:shadow-lg`}
                  >
                    {isLoading ? '처리 중...' : checkResult?.has_password ? '비밀번호 변경' : '가입 완료'}
                  </button>
                </form>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
